import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FiDownload, FiArrowLeft, FiCreditCard } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { getAdminTripsPoliciesFullList, getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsPolicies.css';

const emptyFilters = () => ({
    policy_code: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    company_name: '',
    status: '',
    trigger_type: '',
    activity_start_date_from: '',
    activity_start_date_to: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    trip_modified: '',
    new_trip_start_date_from: '',
    new_trip_start_date_to: '',
    new_trip_end_date_from: '',
    new_trip_end_date_to: '',
});

const TRIP_TRIGGER_TYPE_OPTIONS = [
    { value: 'forecast', label: 'Forecast' },
    { value: 'observation', label: 'Observation' },
    { value: 'forecast and observation', label: 'Forecast and Observation' },
];

const buildQueryFromFilters = (f) => {
    const out = {};
    Object.keys(f).forEach((key) => {
        const value = f[key];
        if (key === 'trip_modified') {
            if (value === 'true' || value === 'false') {
                out[key] = value;
            }
            return;
        }
        if (value && String(value).trim() !== '') {
            out[key] = String(value).trim();
        }
    });
    return out;
};

const buildActivityCombinedAddress = (ai) => {
    const parts = [
        ai.activity_address,
        ai.activity_zip_code,
        ai.activity_city,
        ai.activity_country,
    ]
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter((v) => v !== '');
    return parts.join(', ');
};

const formatWeatherParameter = (value) => {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

const collectWeatherParametersFromSubcoverageTrips = (subcoverageTrips) => {
    if (!Array.isArray(subcoverageTrips) || subcoverageTrips.length === 0) return [];
    const params = subcoverageTrips.flatMap((sub) => {
        if (Array.isArray(sub?.weather_parameter)) return sub.weather_parameter;
        if (sub?.weather_parameter != null && sub.weather_parameter !== '') {
            return [sub.weather_parameter];
        }
        return [];
    });
    return [...new Set(params.map((p) => String(p).trim()).filter(Boolean))];
};

const getFirstSubcoverageFromPolicyEntry = (entry) => {
    const pi = entry?.policy_info || entry || {};
    const ri = pi.reference_info || entry?.reference_info || {};
    if (Array.isArray(ri.subcoverage_trips) && ri.subcoverage_trips.length > 0) {
        return ri.subcoverage_trips[0];
    }
    if (Array.isArray(ri.SubCoverage) && ri.SubCoverage.length > 0) {
        return ri.SubCoverage[0];
    }
    return pi.subcoverage_trip || entry?.subcoverage_trip || {};
};

const resolveWeatherParameterFromPolicyEntry = (entry) => {
    const pi = entry?.policy_info || entry || {};
    const ri = pi.reference_info || entry?.reference_info || {};
    const ai = pi.activity_info || entry?.activity_info || {};
    const subcoverageTrip = getFirstSubcoverageFromPolicyEntry(entry);

    const fromSubcoverageTrips = collectWeatherParametersFromSubcoverageTrips(ri.subcoverage_trips);
    if (fromSubcoverageTrips.length > 0) {
        return formatWeatherParameter(fromSubcoverageTrips);
    }

    return formatWeatherParameter(
        subcoverageTrip.weather_parameter
            ?? pi.weather_parameter
            ?? entry?.weather_parameter
            ?? ai.weather_parameter
            ?? ri.weather_parameter
    );
};

const pickFirstDefined = (...values) => {
    for (const value of values) {
        if (value !== null && value !== undefined && value !== '') {
            return value;
        }
    }
    return '';
};

const policyRowToSheet = (entry) => {
    const pi = entry?.policy_info || entry || {};
    const ci = pi.client_info || entry?.client_info || {};
    const ai = pi.activity_info || entry?.activity_info || {};
    const pr = pi.partner_info || entry?.partner_info || {};
    const sub = getFirstSubcoverageFromPolicyEntry(entry);
    const activityCombinedAddress = buildActivityCombinedAddress(ai);
    return {
        policy_code: pi.policy_code ?? '',
        client_code: ci.client_code ?? '',
        client_first_name: ci.first_name ?? '',
        client_last_name: ci.last_name ?? '',
        status: pi.status ?? '',
        preferred_language: pi.preferred_language ?? '',
        uw_date: pi.uw_date ?? '',
        client_email: ci.email ?? '',
        client_phone: ci.phone_number ?? '',
        account_holder_name: ci.account_holder_name ?? '',
        company_name: pr.company_name ?? '',
        partner_email: pr.email ?? ai.partner_email ?? '',
        reservation_number: ai.reservation_number ?? '',
        activity_name: ai.name ?? '',
        activity_code: ai.activity_code ?? pi.activity_code ?? '',
        activity_start_date: ai.activity_start_date ?? '',
        activity_end_date: ai.activity_end_date ?? '',
        activity_city: ai.activity_city ?? pi.activity_city ?? '',
        activity_country: ai.activity_country ?? pi.activity_country ?? '',
        supplier_name: ai.supplier_name ?? pi.supplier_name ?? '',
        trip_price: ai.activity_price ?? pi.trip_price ?? '',
        new_trip_price: ai.new_trip_price ?? '',
        new_trip_start_date: ai.new_trip_start_date ?? '',
        new_trip_end_date: ai.new_trip_end_date ?? '',
        effective_start_date: ai.trip_effective_start_date ?? pi.effective_start_date ?? '',
        effective_end_date: ai.trip_effective_end_date ?? pi.effective_end_date ?? '',
        new_trip_effective_start_date: ai.new_trip_effective_start_date ?? '',
        new_trip_effective_end_date: ai.new_trip_effective_end_date ?? '',
        trip_number_nights: pi.trip_number_nights ?? '',
        nb_nights_triggered: pi.nb_nights_triggered ?? '',
        new_trip_number_nights: pi.new_trip_number_nights ?? '',
        new_nb_nights_triggered: pi.new_nb_nights_triggered ?? '',
        trip_modified: ai.trip_modified ?? pi.trip_modified ?? '',
        activity_combined_address: activityCombinedAddress,
        weather_parameter: resolveWeatherParameterFromPolicyEntry(entry),
        temperature_trigger_hour_inf: pickFirstDefined(
            pi.temperature_trigger_hour_inf,
            entry?.temperature_trigger_hour_inf,
            sub.temperature_trigger_hour_inf
        ),
        temperature_trigger_number_hours: pickFirstDefined(
            pi.temperature_trigger_number_hours,
            entry?.temperature_trigger_number_hours,
            sub.temperature_trigger_number_hours
        ),
        price_incl_tsca: pi.price_incl_tsca ?? '',
        orakle_price_ratio_incl_tsca: pi.orakle_price_ratio_incl_tsca ?? '',
        daily_sum_insured_maintain: pi.daily_sum_insured_maintain ?? '',
        daily_sum_insured_cancellation: pi.daily_sum_insured_cancellation ?? '',
        total_sum_insured_maintain: pi.total_sum_insured_maintain ?? '',
        total_sum_insured_cancellation: pi.total_sum_insured_cancellation ?? '',
    };
};

const AdminTripsPoliciesExport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initial = useMemo(
        () => (location.state?.filters && typeof location.state.filters === 'object' ? location.state.filters : {}),
        [location.state]
    );

    const [filters, setFilters] = useState(() => {
        const base = emptyFilters();
        Object.keys(base).forEach((k) => {
            if (initial[k] != null && String(initial[k]).trim() !== '') {
                base[k] = String(initial[k]).trim();
            }
        });
        return base;
    });
    const [companyPartnerSearch, setCompanyPartnerSearch] = useState(() => filters.company_name || '');
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (err) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(err, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };
        loadFilterPartners();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleClear = () => {
        setFilters(emptyFilters());
        setCompanyPartnerSearch('');
        setError(null);
    };

    const handleExport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const companyName = (filters.company_name || companyPartnerSearch).trim();
            const query = buildQueryFromFilters({ ...filters, company_name: companyName });
            const data = await getAdminTripsPoliciesFullList(query);
            const rawList = data?.policies ?? data?.data?.policies ?? (Array.isArray(data) ? data : []);
            const list = Array.isArray(rawList) ? rawList : [];
            if (list.length === 0) {
                setError('Aucune police à exporter pour ces filtres.');
                return;
            }
            const rows = list.map(policyRowToSheet);
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Polices');
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
            XLSX.writeFile(workbook, `trips_policies_export_${stamp}.xlsx`);
        } catch (err) {
            setError(handleApiError(err, 'Erreur lors de l\'export Excel'));
        } finally {
            setLoading(false);
        }
    }, [filters, companyPartnerSearch]);

    return (
        <div className="dashboard-page admin-trips-policies-page">
            <Card>
                <Card.Body>
                    <div className="page-header mb-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                            <div>
                                <h2 className="page-title mb-1">
                                    <FiCreditCard className="me-2" />
                                    Export Excel — Polices séjours
                                </h2>
                                <p className="text-muted mb-0">
                                    Données issues de <code>GET /api/trips/policies</code> (filtres optionnels), fichier{' '}
                                    <strong>.xlsx</strong> généré côté navigateur.
                                </p>
                            </div>
                            <Button variant="outline-secondary" className="d-flex align-items-center" onClick={() => navigate('/admin/trips/policies')}>
                                <FiArrowLeft className="me-2" />
                                Retour à la liste
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Card className="mb-4">
                        <Card.Header>
                            <h6 className="mb-0">Filtres (optionnels)</h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control name="policy_code" value={filters.policy_code} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control name="reservation_number" value={filters.reservation_number} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control name="client_first_name" value={filters.client_first_name} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control name="client_last_name" value={filters.client_last_name} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom de l&apos;entreprise</Form.Label>
                                        <PartnerCompanyAutocomplete
                                            value={filters.company_name}
                                            onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                            onInputChange={setCompanyPartnerSearch}
                                            options={partnerCompanyOptions}
                                            loading={filterPartnersLoading}
                                            error={filterPartnersError}
                                            placeholder="Rechercher un partenaire…"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Statut</Form.Label>
                                        <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                                            <option value="">Tous les statuts</option>
                                            <option value="Created">Created</option>
                                            <option value="Paid">Paid</option>
                                            <option value="CG confirmed">CG confirmed</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type de déclenchement</Form.Label>
                                        <Form.Select
                                            name="trigger_type"
                                            value={filters.trigger_type}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">Tous les types</option>
                                            {TRIP_TRIGGER_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité (de)</Form.Label>
                                        <Form.Control type="date" name="activity_start_date_from" value={filters.activity_start_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité (à)</Form.Label>
                                        <Form.Control type="date" name="activity_start_date_to" value={filters.activity_start_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_from" value={filters.activity_end_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_to" value={filters.activity_end_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Dates séjour modifiées</Form.Label>
                                        <Form.Select name="trip_modified" value={filters.trip_modified} onChange={handleFilterChange}>
                                            <option value="">Tous</option>
                                            <option value="true">Oui</option>
                                            <option value="false">Non</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouveau début séjour (de)</Form.Label>
                                        <Form.Control type="date" name="new_trip_start_date_from" value={filters.new_trip_start_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouveau début séjour (à)</Form.Label>
                                        <Form.Control type="date" name="new_trip_start_date_to" value={filters.new_trip_start_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouvelle fin séjour (de)</Form.Label>
                                        <Form.Control type="date" name="new_trip_end_date_from" value={filters.new_trip_end_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouvelle fin séjour (à)</Form.Label>
                                        <Form.Control type="date" name="new_trip_end_date_to" value={filters.new_trip_end_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex flex-wrap gap-2 justify-content-end">
                                <Button variant="outline-secondary" onClick={handleClear} disabled={loading}>
                                    Réinitialiser les filtres
                                </Button>
                                <Button variant="success" className="d-flex align-items-center" onClick={handleExport} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" animation="border" />
                                            Export en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className="me-2" />
                                            Télécharger Excel
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsPoliciesExport;
